// AGC: OrderBuilderModal - Modal for creating and configuring purchase orders
// AGC: Allows selecting items, adjusting quantities, adding supplier info
// AGC: Integrates with purchaseOrderService for creating orders

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { clampDecimals } from '../../utils'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  ShoppingCart,
  Check,
  AlertTriangle,
  Package,
  Calendar,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  ChevronDown,
  Minus,
  Plus,
  Send,
  Save,
  Loader2,
  Star,
  Heart,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useFarms } from '../../context'
import {
  createPurchaseOrder,
  sendPurchaseOrder,
  getFavoriteSuppliers,
  addFavoriteSupplier,
  type FavoriteSupplier,
} from '../../services/purchaseOrderService'
import {
  sharePurchaseOrder,
  getDefaultPurchaseOrderTranslations,
} from '../../services/quotePdfService'
import { 
  type InputCategory, 
  type StockStatus,
  type PurchaseOrderSupplier,
  type PurchaseOrderItem,
  INPUT_CATEGORIES,
  getInputCategoryInfo,
} from '../../types/inventory'
import toast from 'react-hot-toast'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface OrderableItem {
  inputId: string
  name: string
  category: InputCategory
  unit: string
  totalRequired: number
  inStock: number
  needToBuy: number
  status: StockStatus
  lotsNeeding: { id: string; name: string; required: number }[]
  estimatedCostPerUnit?: number
}

interface OrderBuilderModalProps {
  isOpen: boolean
  onClose: () => void
  items: OrderableItem[]
  onOrderCreated?: (orderId: string) => void
}

interface SelectedItem {
  inputId: string
  quantity: number
  included: boolean
  costPerUnit: number
}

// =============================================================================
// AGC: HELPER COMPONENTS
// =============================================================================

const StatusBadge: React.FC<{ status: StockStatus }> = ({ status }) => {
  const { t } = useTranslation()
  
  const styles: Record<StockStatus, { bg: string; text: string }> = {
    out: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
    critical: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
    low: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
    ok: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    overstock: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
  }
  
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${styles[status].bg} ${styles[status].text}`}>
      {t(`inputs.stockStatus.${status}`)}
    </span>
  )
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const OrderBuilderModal: React.FC<OrderBuilderModalProps> = ({
  isOpen,
  onClose,
  items,
  onOrderCreated,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { currentFarm, farms } = useFarms()
  
  // AGC: State
  const [step, setStep] = useState<'items' | 'supplier' | 'review'>('items')
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map())
  const [statusFilter, setStatusFilter] = useState<StockStatus | 'all' | 'buy'>('all')
  const [categoryFilter, setCategoryFilter] = useState<InputCategory | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [supplier, setSupplier] = useState<PurchaseOrderSupplier>({
    name: '',
    contact: '',
    phone: '',
    email: '',
    address: '',
  })
  const [expectedDelivery, setExpectedDelivery] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [favorites, setFavorites] = useState<FavoriteSupplier[]>([])
  const [saveAsFavorite, setSaveAsFavorite] = useState(false)
  
  // AGC: Initialize only when modal opens (isOpen transitions to true).
  // AGC: Do NOT depend on `items` — it gets a new reference on parent re-render (scroll),
  // AGC: which would reset all user selections.
  const prevOpenRef = React.useRef(false)
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      const initial = new Map<string, SelectedItem>()
      items.forEach(item => {
        initial.set(item.inputId, {
          inputId: item.inputId,
          quantity: item.needToBuy > 0 ? Math.ceil(item.needToBuy) : 1,
          included: item.status === 'out' || item.status === 'critical',
          costPerUnit: item.estimatedCostPerUnit || 0,
        })
      })
      setSelectedItems(initial)
      setStep('items')
      setSearchTerm('')
      setSaveAsFavorite(false)
      getFavoriteSuppliers()
        .then(setFavorites)
        .catch(() => setFavorites([]))
    }
    prevOpenRef.current = isOpen
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectFavorite = useCallback((fav: FavoriteSupplier) => {
    setSupplier({
      name: fav.name,
      contact: fav.contact || '',
      phone: fav.phone || '',
      email: fav.email || '',
      address: fav.address || '',
    })
    setSaveAsFavorite(false)
  }, [])
  
  // AGC: Filter items — search, status, category. Selected items ALWAYS visible + sorted to top.
  const filteredItems = useMemo(() => {
    const result = items.filter(item => {
      const isSelected = selectedItems.get(item.inputId)?.included
      if (isSelected) return true
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'buy' ? (item.status === 'out' || item.status === 'critical') : item.status === statusFilter)
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter
      const matchesSearch = !searchTerm || item.name.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesStatus && matchesCategory && matchesSearch
    })
    return result.sort((a, b) => {
      const aSelected = selectedItems.get(a.inputId)?.included ? 1 : 0
      const bSelected = selectedItems.get(b.inputId)?.included ? 1 : 0
      return bSelected - aSelected
    })
  }, [items, statusFilter, categoryFilter, searchTerm, selectedItems])

  const availableCategories = useMemo(() => {
    const cats = new Set<InputCategory>()
    items.forEach(i => cats.add(i.category))
    return Array.from(cats)
  }, [items])

  // AGC: All selected items for order — not dependent on filters
  const itemsToOrder = useMemo(() => {
    return items.filter(item => {
      const selected = selectedItems.get(item.inputId)
      return selected?.included && selected.quantity > 0
    })
  }, [items, selectedItems])
  
  // AGC: Calculate total estimated cost from user-editable costPerUnit
  const totalCost = useMemo(() => {
    return itemsToOrder.reduce((sum, item) => {
      const selected = selectedItems.get(item.inputId)
      return sum + (selected?.costPerUnit || 0) * (selected?.quantity || 0)
    }, 0)
  }, [itemsToOrder, selectedItems])
  
  // AGC: Toggle item selection
  const toggleItem = useCallback((inputId: string) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      const existing = newMap.get(inputId)
      if (existing) {
        newMap.set(inputId, { ...existing, included: !existing.included })
      }
      return newMap
    })
  }, [])
  
  // AGC: Update item quantity
  const updateQuantity = useCallback((inputId: string, delta: number) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      const existing = newMap.get(inputId)
      if (existing) {
        const newQty = Math.max(1, existing.quantity + delta)
        newMap.set(inputId, { ...existing, quantity: newQty, included: true })
      }
      return newMap
    })
  }, [])
  
  const setQuantity = useCallback((inputId: string, quantity: number) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      const existing = newMap.get(inputId)
      if (existing) {
        newMap.set(inputId, { ...existing, quantity: Math.max(0, quantity), included: quantity > 0 })
      }
      return newMap
    })
  }, [])

  const setCostPerUnit = useCallback((inputId: string, cost: number) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      const existing = newMap.get(inputId)
      if (existing) {
        newMap.set(inputId, { ...existing, costPerUnit: Math.max(0, cost) })
      }
      return newMap
    })
  }, [])

  // AGC: Select/deselect all
  const selectAll = useCallback((include: boolean) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      filteredItems.forEach(item => {
        const existing = newMap.get(item.inputId)
        if (existing) {
          newMap.set(item.inputId, { ...existing, included: include })
        }
      })
      return newMap
    })
  }, [filteredItems])
  
  // AGC: Handle create order
  const handleCreateOrder = async (share: boolean = false) => {
    // AGC: Resolve farmId — fall back to first farm if currentFarm not set yet
    const resolvedFarmId = currentFarm?.id || farms[0]?.id
    if (!user?.uid || !resolvedFarmId) {
      toast.error('Usuário ou fazenda não identificados')
      return
    }
    
    if (itemsToOrder.length === 0) {
      toast.error(t('inputs.orders.builder.selectAtLeastOne'))
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // AGC: Prepare order items
      const orderItems: Omit<PurchaseOrderItem, 'status' | 'receivedQuantity'>[] = itemsToOrder.map(item => {
        const selected = selectedItems.get(item.inputId)!
        return {
          inputId: item.inputId,
          inputName: item.name,
          category: item.category,
          unit: item.unit,
          requestedQuantity: selected.quantity,
          estimatedCostPerUnit: selected.costPerUnit || 0,
          estimatedTotalCost: (selected.costPerUnit || 0) * selected.quantity,
          lotsNeeding: item.lotsNeeding.map(lot => ({
            lotId: lot.id,
            lotName: lot.name,
            quantity: lot.required,
          })),
        }
      })
      
      // AGC: Create order
      const order = await createPurchaseOrder(user.uid, resolvedFarmId, {
        items: orderItems,
        supplier: supplier.name ? supplier : undefined,
        expectedDeliveryDate: expectedDelivery || undefined,
        notes: notes || undefined,
      })
      
      if (saveAsFavorite && supplier.name) {
        try {
          await addFavoriteSupplier({
            name: supplier.name,
            contact: supplier.contact || undefined,
            phone: supplier.phone || undefined,
            email: supplier.email || undefined,
            address: supplier.address || undefined,
          })
        } catch {
          // AGC: Non-blocking — order was already created
        }
      }

      if (share) {
        await sendPurchaseOrder(user.uid, resolvedFarmId, order.id, 'whatsapp')
        try {
          await sharePurchaseOrder(
            { order, farmName: currentFarm.name || 'Minha Fazenda' },
            getDefaultPurchaseOrderTranslations(),
          )
        } catch (err) {
          console.error('AGC: Error sharing order PDF:', err)
        }
        toast.success('Pedido criado e compartilhado!')
      } else {
        toast.success('Pedido salvo como rascunho!')
      }

      onOrderCreated?.(order.id)
      onClose()
      
    } catch (error) {
      console.error('AGC: Error creating order:', error)
      toast.error('Erro ao criar pedido')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const hasSelections = useMemo(() => {
    return Array.from(selectedItems.values()).some(s => s.included && s.quantity > 0)
  }, [selectedItems])

  const handleSafeClose = useCallback(() => {
    if (hasSelections) {
      const confirmed = window.confirm(
        t('inputs.orders.builder.closeWarning') ||
        'Você tem itens selecionados. Se sair, perderá a seleção. Deseja sair?'
      )
      if (!confirmed) return
    }
    onClose()
  }, [hasSelections, onClose, t])

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
        onClick={(e) => e.target === e.currentTarget && handleSafeClose()}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* AGC: Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {t('inputs.orders.builder.title')}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {itemsToOrder.length} {t('inputs.orders.totalItems').toLowerCase()}
                </p>
              </div>
            </div>
            <button
              onClick={handleSafeClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* AGC: Step Indicators */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700/50">
            {['items', 'supplier', 'review'].map((s, idx) => (
              <React.Fragment key={s}>
                <button
                  onClick={() => setStep(s as typeof step)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    step === s
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                    step === s 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                  }`}>
                    {idx + 1}
                  </span>
                  {s === 'items' && t('inputs.orders.builder.selectItems')}
                  {s === 'supplier' && t('inputs.orders.supplier.title')}
                  {s === 'review' && t('inputs.orders.orderDetails')}
                </button>
                {idx < 2 && <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />}
              </React.Fragment>
            ))}
          </div>
          
          {/* AGC: Content */}
          <div className="flex-1 overflow-y-auto">
            {step === 'items' && (
              <div className="flex flex-col h-full">
                {/* AGC: Search + filters — sticky so items scroll below on keyboard open */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-4 pt-4 pb-2 space-y-3 border-b border-gray-100 dark:border-gray-700/50">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('inputs.orders.builder.searchItems') || 'Buscar insumo...'}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                />

                {/* AGC: Filters */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('inputs.orders.builder.filterByStatus')}
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as StockStatus | 'all' | 'buy')}
                      className="w-full px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs"
                    >
                      <option value="all">{t('common.all')}</option>
                      <option value="buy">{t('inputs.stockStatus.out')}</option>
                      <option value="low">{t('inputs.stockStatus.low')}</option>
                      <option value="ok">OK</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('inputs.orders.builder.filterByCategory')}
                    </label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value as InputCategory | 'all')}
                      className="w-full px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs"
                    >
                      <option value="all">{t('common.all')}</option>
                      {availableCategories.map(cat => {
                        const info = getInputCategoryInfo(cat)
                        return <option key={cat} value={cat}>{info?.namePt || cat}</option>
                      })}
                    </select>
                  </div>
                </div>
                
                {/* AGC: Select/Deselect All */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => selectAll(true)}
                    className="px-2 py-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                  >
                    {t('inputs.orders.builder.includeAll')}
                  </button>
                  <button
                    onClick={() => selectAll(false)}
                    className="px-2 py-1 text-[10px] font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    {t('inputs.orders.builder.excludeAll')}
                  </button>
                  <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                    {itemsToOrder.length}/{filteredItems.length} {t('common.selected')}
                  </span>
                </div>
                </div>

                {/* AGC: Items List — scrollable below sticky header */}
                <div className="flex-1 overflow-y-auto px-4 pb-4 pt-2 space-y-2">
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('inputs.noInputs')}
                      </p>
                    </div>
                  ) : (
                    filteredItems.map(item => {
                      const selected = selectedItems.get(item.inputId)
                      const isIncluded = selected?.included || false
                      const quantity = selected?.quantity || 0
                      const cost = selected?.costPerUnit || 0
                      const catInfo = getInputCategoryInfo(item.category)
                      const CategoryIcon = catInfo?.iconComponent

                      return (
                        <div
                          key={item.inputId}
                          className={`p-3 rounded-xl border transition-colors ${
                            isIncluded
                              ? 'border-emerald-200 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                          }`}
                        >
                          {/* AGC: Row 1 — checkbox + name + status */}
                          <div className="flex items-center gap-3 mb-2">
                            <button
                              onClick={() => toggleItem(item.inputId)}
                              className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                                isIncluded
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600'
                              }`}
                            >
                              {isIncluded && <Check className="w-3.5 h-3.5" />}
                            </button>
                            {CategoryIcon && (
                              <div
                                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${catInfo?.color}15` }}
                              >
                                <CategoryIcon className="w-3.5 h-3.5" style={{ color: catInfo?.color }} />
                              </div>
                            )}
                            <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white">
                              {item.name}
                            </span>
                            <StatusBadge status={item.status} />
                          </div>

                          {/* AGC: Row 2 — quantity + price per unit */}
                          <div className="flex items-center gap-2 ml-8">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => updateQuantity(item.inputId, -1)}
                                disabled={quantity <= 1}
                                className="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 disabled:opacity-50"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                inputMode="decimal"
                                value={quantity}
                                onChange={(e) => setQuantity(item.inputId, clampDecimals(parseFloat(e.target.value) || 0))}
                                className="w-14 px-1 py-1 text-center text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded"
                              />
                              <button
                                onClick={() => updateQuantity(item.inputId, 1)}
                                className="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                              <span className="text-[10px] text-gray-400 w-6">{item.unit}</span>
                            </div>
                            <div className="flex items-center gap-1 ml-auto">
                              <span className="text-[10px] text-gray-400">R$</span>
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                value={cost || ''}
                                onChange={(e) => setCostPerUnit(item.inputId, clampDecimals(parseFloat(e.target.value) || 0))}
                                placeholder="0,00"
                                className="w-16 px-1 py-1 text-center text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded"
                              />
                              <span className="text-[10px] text-gray-400">/{item.unit}</span>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )}
            
            {step === 'supplier' && (
              <div className="space-y-4 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {t('inputs.orders.supplier.optional')}
                </p>

                {favorites.length > 0 && (
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      <Star className="w-3.5 h-3.5 text-amber-500" />
                      {t('inputs.orders.supplier.favorites')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {favorites.map(fav => (
                        <button
                          key={fav.id}
                          onClick={() => selectFavorite(fav)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            supplier.name === fav.name
                              ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-amber-300 dark:hover:border-amber-600'
                          }`}
                        >
                          <Star className="w-3 h-3 text-amber-500" />
                          {fav.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <User className="w-3.5 h-3.5" />
                    {t('inputs.orders.supplier.name')}
                  </label>
                  <input
                    type="text"
                    value={supplier.name}
                    onChange={(e) => setSupplier(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nome do fornecedor..."
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                  />
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <User className="w-3.5 h-3.5" />
                    {t('inputs.orders.supplier.contact')}
                  </label>
                  <input
                    type="text"
                    value={supplier.contact || ''}
                    onChange={(e) => setSupplier(prev => ({ ...prev, contact: e.target.value }))}
                    placeholder="Pessoa de contato..."
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      <Phone className="w-3.5 h-3.5" />
                      {t('inputs.orders.supplier.phone')}
                    </label>
                    <input
                      type="tel"
                      value={supplier.phone || ''}
                      onChange={(e) => setSupplier(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(00) 00000-0000"
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      <Mail className="w-3.5 h-3.5" />
                      {t('inputs.orders.supplier.email')}
                    </label>
                    <input
                      type="email"
                      value={supplier.email || ''}
                      onChange={(e) => setSupplier(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@exemplo.com"
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {t('inputs.orders.supplier.address')}
                  </label>
                  <input
                    type="text"
                    value={supplier.address || ''}
                    onChange={(e) => setSupplier(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Endereço..."
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                  />
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {t('inputs.orders.expectedDelivery')}
                  </label>
                  <input
                    type="date"
                    value={expectedDelivery}
                    onChange={(e) => setExpectedDelivery(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm"
                  />
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    {t('inputs.orders.builder.notes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('inputs.orders.builder.notesPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm resize-none"
                  />
                </div>

                {supplier.name && !favorites.some(f => f.name === supplier.name) && (
                  <label className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveAsFavorite}
                      onChange={(e) => setSaveAsFavorite(e.target.checked)}
                      className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex items-center gap-1.5">
                      <Heart className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800 dark:text-amber-300">
                        {t('inputs.orders.supplier.saveAsFavorite')}
                      </span>
                    </div>
                  </label>
                )}
              </div>
            )}
            
            {step === 'review' && (
              <div className="space-y-4 p-4">
                {/* AGC: Summary */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    {t('inputs.orders.detail.orderInfo')}
                  </h3>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">{t('inputs.orders.totalItems')}:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{itemsToOrder.length}</span>
                    </div>
                    {totalCost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('inputs.orders.estimatedCost')}:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          R$ {totalCost.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {supplier.name && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('inputs.orders.supplier.name')}:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{supplier.name}</span>
                      </div>
                    )}
                    {expectedDelivery && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('inputs.orders.expectedDelivery')}:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Date(expectedDelivery).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* AGC: Items List */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    {t('inputs.orders.detail.itemsList')}
                  </h3>
                  
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">
                      <div className="col-span-6">{t('inputs.inputName')}</div>
                      <div className="col-span-3 text-right">{t('inputs.orders.builder.quantity')}</div>
                      <div className="col-span-3 text-right">{t('inputs.lotsNeeding')}</div>
                    </div>
                    
                    {itemsToOrder.map(item => {
                      const selected = selectedItems.get(item.inputId)
                      const catInfo = getInputCategoryInfo(item.category)
                      const CategoryIcon = catInfo?.iconComponent
                      
                      return (
                        <div
                          key={item.inputId}
                          className="grid grid-cols-12 gap-1 px-3 py-2 border-t border-gray-100 dark:border-gray-700/50 text-xs"
                        >
                          <div className="col-span-6 flex items-center gap-2">
                            {CategoryIcon && (
                              <CategoryIcon 
                                className="w-3.5 h-3.5" 
                                style={{ color: catInfo?.color }} 
                              />
                            )}
                            <span className="truncate text-gray-900 dark:text-white">{item.name}</span>
                          </div>
                          <div className="col-span-3 text-right text-gray-700 dark:text-gray-300">
                            {selected?.quantity} {item.unit}
                          </div>
                          <div className="col-span-3 text-right text-gray-500 dark:text-gray-400">
                            {item.lotsNeeding.length}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                
                {notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      {t('inputs.orders.builder.notes')}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                      {notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* AGC: Footer Actions */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2">
              {step !== 'items' && (
                <button
                  onClick={() => setStep(step === 'review' ? 'supplier' : 'items')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t('common.back')}
                </button>
              )}
              
              <div className="flex-1" />
              
              {step === 'items' && (
                <button
                  onClick={() => setStep('supplier')}
                  disabled={itemsToOrder.length === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.next')}
                  <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </button>
              )}
              
              {step === 'supplier' && (
                <button
                  onClick={() => setStep('review')}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700"
                >
                  {t('common.next')}
                  <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </button>
              )}
              
              {step === 'review' && (
                <>
                  <button
                    onClick={() => handleCreateOrder(false)}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {t('inputs.orders.actions.saveDraft')}
                  </button>
                  
                  <button
                    onClick={() => handleCreateOrder(true)}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {t('inputs.orders.actions.shareWhatsApp')}
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default OrderBuilderModal
